import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, formatDistanceToNow } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Sorting utility for dynamic column and direction
export function sortData<T>(data: T[], sortKey: string | null, sortDirection: "asc" | "desc" | null): T[] {
  if (!sortKey || !sortDirection) {
    return data;
  }

  return [...data].sort((a: any, b: any) => {
    // Handle nested properties like "application.name"
    const keysA = sortKey.split(".");
    const keysB = sortKey.split(".");
    
    let valueA = a;
    let valueB = b;
    
    // Access nested properties
    for (const key of keysA) {
      if (valueA && typeof valueA === "object") {
        valueA = valueA[key];
      } else {
        valueA = undefined;
        break;
      }
    }
    
    for (const key of keysB) {
      if (valueB && typeof valueB === "object") {
        valueB = valueB[key];
      } else {
        valueB = undefined;
        break;
      }
    }
    
    // Handle dates
    if (valueA instanceof Date && valueB instanceof Date) {
      return sortDirection === "asc" 
        ? valueA.getTime() - valueB.getTime() 
        : valueB.getTime() - valueA.getTime();
    }
    
    // Handle string dates
    if (typeof valueA === "string" && typeof valueB === "string") {
      // Check if values look like dates
      const dateA = new Date(valueA);
      const dateB = new Date(valueB);
      
      if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
        return sortDirection === "asc" 
          ? dateA.getTime() - dateB.getTime() 
          : dateB.getTime() - dateA.getTime();
      }
    }
    
    // Handle numbers
    if (typeof valueA === "number" && typeof valueB === "number") {
      return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
    }
    
    // Handle strings (case-insensitive)
    if (typeof valueA === "string" && typeof valueB === "string") {
      return sortDirection === "asc" 
        ? valueA.localeCompare(valueB, undefined, { sensitivity: "base" })
        : valueB.localeCompare(valueA, undefined, { sensitivity: "base" });
    }
    
    // Handle nullish values
    if (valueA === null || valueA === undefined) {
      return sortDirection === "asc" ? -1 : 1;
    }
    
    if (valueB === null || valueB === undefined) {
      return sortDirection === "asc" ? 1 : -1;
    }
    
    // Default case
    return 0;
  });
}

// Module-level singleton — updated by CurrencyProvider on app load
let _activeCurrency = 'USD';
export function setActiveCurrency(code: string) { _activeCurrency = code; }
export function getActiveCurrency() { return _activeCurrency; }

export function formatCurrency(amount: number, currency?: string): string {
  const code = currency || _activeCurrency;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  }
}

export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }
    
    return format(dateObj, 'MMM dd, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
}

export function formatDateDistance(date: Date | string | null | undefined): string {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }
    
    return formatDistanceToNow(dateObj, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting date distance:', error);
    return 'Invalid date';
  }
}

export function calculateTimeDifference(date: Date | string | null | undefined): string {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      return 'Invalid date';
    }
    
    const now = new Date();
    const diffInDays = Math.floor((dateObj.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays < 0) {
      return 'Overdue';
    } else if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Tomorrow';
    } else if (diffInDays < 7) {
      return `In ${diffInDays} days`;
    } else if (diffInDays < 30) {
      return `In ${Math.floor(diffInDays / 7)} weeks`;
    } else {
      return `In ${Math.floor(diffInDays / 30)} months`;
    }
  } catch (error) {
    console.error('Error calculating time difference:', error);
    return 'Invalid date';
  }
}

/**
 * Calculate the number of days until a given date.
 * Uses Math.floor for consistent calculation across the entire application.
 * @param date - The target date
 * @returns Number of days until the date (negative if overdue)
 */
export function calculateDaysUntil(date: Date | string | null | undefined): number {
  if (!date) return 0;
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if the date is valid
    if (isNaN(dateObj.getTime())) {
      return 0;
    }
    
    const now = new Date();
    // Standardize on Math.floor for consistent behavior across all components
    return Math.floor((dateObj.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  } catch (error) {
    console.error('Error calculating days until date:', error);
    return 0;
  }
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

// Generate app icon based on the first letter of the app name
export function getAppIcon(appName: string | null): string {
  if (!appName || appName.trim() === '') return 'ri-apps-2-line';
  
  // Special cases for well-known applications (optional, for better UX)
  const specialCases: Record<string, string> = {
    'slack': 'ri-slack-line',
    'microsoft': 'ri-microsoft-line',
    'adobe': 'ri-adobe-line',
    'dropbox': 'ri-dropbox-line',
    'zoom': 'ri-zoom-line',
    'github': 'ri-github-fill',
    'google': 'ri-google-line',
    'figma': 'ri-brush-line',
  };
  
  const lowerName = appName.toLowerCase().trim();
  if (specialCases[lowerName]) {
    return specialCases[lowerName];
  }
  
  // For all other apps, we'll return a generic icon since we're using first letter as text
  // The actual letter will be displayed as text, not an icon
  return 'ri-apps-2-line';
}

// Generate app color based on the first letter of the app name
export function getAppColor(appName: string | null): string {
  if (!appName || appName.trim() === '') return 'bg-blue-100 text-blue-600';
  
  // Special cases for well-known applications (optional, for brand consistency)
  const specialCases: Record<string, string> = {
    'slack': 'bg-blue-100 text-blue-600',
    'microsoft': 'bg-indigo-100 text-indigo-600',
    'adobe': 'bg-red-100 text-red-600',
    'dropbox': 'bg-emerald-100 text-emerald-600',
    'zoom': 'bg-indigo-100 text-indigo-600',
    'github': 'bg-gray-100 text-gray-800',
    'google': 'bg-blue-100 text-blue-600',
    'figma': 'bg-purple-100 text-purple-600',
  };
  
  const lowerName = appName.toLowerCase().trim();
  if (specialCases[lowerName]) {
    return specialCases[lowerName];
  }
  
  // Generate color based on first letter for consistent, predictable colors
  const firstLetter = appName.charAt(0).toUpperCase();
  const colorMap: Record<string, string> = {
    'A': 'bg-red-100 text-red-600',
    'B': 'bg-blue-100 text-blue-600',
    'C': 'bg-green-100 text-green-600',
    'D': 'bg-purple-100 text-purple-600',
    'E': 'bg-emerald-100 text-emerald-600',
    'F': 'bg-indigo-100 text-indigo-600',
    'G': 'bg-amber-100 text-amber-600',
    'H': 'bg-pink-100 text-pink-600',
    'I': 'bg-cyan-100 text-cyan-600',
    'J': 'bg-teal-100 text-teal-600',
    'K': 'bg-lime-100 text-lime-600',
    'L': 'bg-orange-100 text-orange-600',
    'M': 'bg-violet-100 text-violet-600',
    'N': 'bg-rose-100 text-rose-600',
    'O': 'bg-sky-100 text-sky-600',
    'P': 'bg-fuchsia-100 text-fuchsia-600',
    'Q': 'bg-slate-100 text-slate-600',
    'R': 'bg-red-100 text-red-600',
    'S': 'bg-blue-100 text-blue-600',
    'T': 'bg-green-100 text-green-600',
    'U': 'bg-purple-100 text-purple-600',
    'V': 'bg-emerald-100 text-emerald-600',
    'W': 'bg-indigo-100 text-indigo-600',
    'X': 'bg-amber-100 text-amber-600',
    'Y': 'bg-pink-100 text-pink-600',
    'Z': 'bg-cyan-100 text-cyan-600',
  };
  
  return colorMap[firstLetter] || 'bg-blue-100 text-blue-600';
}

// Get the first letter of an app name for logo display
export function getAppLetter(appName: string | null): string {
  if (!appName || appName.trim() === '') return 'A';
  return appName.charAt(0).toUpperCase();
}

export function getLicenseStatusColor(status: string | null | undefined): string {
  if (!status) return 'bg-slate-100 text-slate-800';
  
  const statusColorMap: Record<string, string> = {
    'active': 'bg-green-100 text-green-800',
    'inactive': 'bg-red-100 text-red-800',
    'pending': 'bg-amber-100 text-amber-800',
    'expired': 'bg-red-100 text-red-800',
    'trial': 'bg-purple-100 text-purple-800',
    'discontinued': 'bg-slate-200 text-slate-800',
  };
  
  try {
    return statusColorMap[status.toLowerCase()] || 'bg-slate-100 text-slate-800';
  } catch (error) {
    console.error('Error getting license status color:', error);
    return 'bg-slate-100 text-slate-800';
  }
}

export function getActivityIcon(type: string | null | undefined): { icon: string, color: string } {
  if (!type) return { icon: 'ri-information-line', color: 'bg-slate-500' };
  
  const typeMap: Record<string, { icon: string, color: string }> = {
    'app_added': { icon: 'ri-user-add-line', color: 'bg-blue-500' },
    'price_change': { icon: 'ri-price-tag-3-line', color: 'bg-amber-500' },
    'license_changed': { icon: 'ri-check-line', color: 'bg-green-500' },
    'alert': { icon: 'ri-alarm-warning-line', color: 'bg-red-500' },
    'app_updated': { icon: 'ri-refresh-line', color: 'bg-blue-500' },
    'license_added': { icon: 'ri-add-line', color: 'bg-green-500' },
    'license_deleted': { icon: 'ri-delete-bin-line', color: 'bg-red-500' },
    'license_assigned': { icon: 'ri-user-add-line', color: 'bg-blue-500' },
    'license_unassigned': { icon: 'ri-user-unfollow-line', color: 'bg-amber-500' },
    'user_added': { icon: 'ri-user-add-line', color: 'bg-blue-500' },
    'user_removed': { icon: 'ri-user-unfollow-line', color: 'bg-red-500' },
    'recommendation_created': { icon: 'ri-lightbulb-line', color: 'bg-amber-500' },
    'recommendation_updated': { icon: 'ri-check-double-line', color: 'bg-green-500' },
  };
  
  return typeMap[type] || { icon: 'ri-information-line', color: 'bg-slate-500' };
}

export function getRenewalUrgencyColor(daysLeft: number): string {
  if (daysLeft < 0) return 'bg-red-100 text-red-800';
  if (daysLeft < 7) return 'bg-red-100 text-red-800';
  if (daysLeft < 15) return 'bg-amber-100 text-amber-800';
  return 'bg-green-100 text-green-800';
}

/**
 * Checks if a license is expired based on its renewal date
 * @param renewalDate The license renewal date
 * @returns true if the license is expired, false otherwise
 */
export function isLicenseExpired(renewalDate: Date | string | null | undefined): boolean {
  if (!renewalDate) return false;
  
  try {
    const renewalDateObj = typeof renewalDate === 'string' ? new Date(renewalDate) : renewalDate;
    if (isNaN(renewalDateObj.getTime())) return false;
    
    const now = new Date();
    return renewalDateObj < now;
  } catch (error) {
    console.error('Error checking if license is expired:', error);
    return false;
  }
}

export function getRecommendationTypeIcon(title: string | null | undefined): { iconName: string, color: string } {
  if (!title) return { iconName: 'Lightbulb', color: 'bg-amber-100 text-amber-600' };
  
  try {
    if (title.toLowerCase().includes('license')) {
      return { iconName: 'DollarSign', color: 'bg-blue-100 text-blue-600' };
    }
    if (title.toLowerCase().includes('storage') || title.toLowerCase().includes('consolidate')) {
      return { iconName: 'FolderOpen', color: 'bg-purple-100 text-purple-600' };
    }
    if (title.toLowerCase().includes('duplicate') || title.toLowerCase().includes('remove')) {
      return { iconName: 'Trash', color: 'bg-red-100 text-red-600' };
    }
    if (title.toLowerCase().includes('upgrade')) {
      return { iconName: 'ArrowUpCircle', color: 'bg-green-100 text-green-600' };
    }
    return { iconName: 'Lightbulb', color: 'bg-amber-100 text-amber-600' };
  } catch (error) {
    console.error('Error getting recommendation type icon:', error);
    return { iconName: 'Lightbulb', color: 'bg-amber-100 text-amber-600' };
  }
}

export function calculateUsagePercentage(assignedSeats: number, totalSeats: number): number {
  if (totalSeats === 0) return 0;
  return Math.round((assignedSeats / totalSeats) * 100);
}

// CSV Export Functions
export function exportToCsv<T>(data: T[], filename: string, headers?: string[]): void {
  if (!data || data.length === 0) {
    console.error("No data to export");
    return;
  }

  // Determine headers from the first object if not provided
  const fields = headers || Object.keys(data[0] || {});
  
  // Create CSV content
  const csvContent = [
    fields.join(','), // CSV header row
    ...data.map(item => {
      return fields.map(field => {
        // Safely get the field value, handling nested properties
        const value = field.split('.').reduce((obj, key) => obj && obj[key as keyof typeof obj], item as any);
        
        // Format values properly for CSV
        if (value === null || value === undefined) return '';
        if (typeof value === 'string') return `"${value.replace(/"/g, '""')}"`;
        if (value instanceof Date) return `"${formatDate(value)}"`;
        return String(value);
      }).join(',');
    })
  ].join('\n');
  
  // Create a blob with the CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  
  // Create a download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// CSV Import Functions
export function parseCSV(csvText: string): Array<Record<string, string>> {
  // Basic CSV parsing (doesn't handle all edge cases but works for simple CSVs)
  const lines = csvText.split('\n');
  const headers = lines[0].split(',').map(header => header.trim());
  const result = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    // Split by comma, but handle quoted values properly
    const values: string[] = [];
    let inQuotes = false;
    let currentValue = '';
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(currentValue);
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    values.push(currentValue); // Add the last value
    
    const row: Record<string, string> = {};
    headers.forEach((header, index) => {
      row[header] = values[index] ? values[index].replace(/^"|"$/g, '') : '';
    });
    
    result.push(row);
  }
  
  return result;
}

// Get initials from a name for avatar display
export function getInitials(name: string): string {
  if (!name) return "?";
  
  const nameParts = name.split(" ");
  if (nameParts.length >= 2) {
    return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
  }
  return nameParts[0][0].toUpperCase();
}
